<script setup lang="ts">

import { onMounted, ref } from "vue";
import { reloadMyGarageViewModel, useViewModelInstance } from "@app/MyGarage";
import { useUser } from "@domain/Security";
import { useConfirm } from "@library/Components";
import { useUserSettings } from "@services/UserSettings";
import { useBackButton } from "@app/Components/Utilities";
import { useVehicleApi } from "@app/MyGarage";
import { usePairingState } from "../HealthMonitor/Types/UsePairingState";
import { VehicleHeaderRow } from "../../Components/Utilities";
import { SampleVehicles } from "@modules/Jupiter/Types/SampleVehicles";
import { isDevelopment, isUAT, isLocal, isNotProduction } from "@utilities/Environments";
import { AutoInstalledState } from "@domain/General/Enums/AutoInstalledState";

import DeveloperGpsCoordinates from "./DeveloperGpsCoordinates.vue";
import SettingsRow from "./SettingsRow.vue";

const model = await useViewModelInstance();
const vehicle = model.vehicle;
const hasRequiredProperties = ref( vehicle.hasRequiredProperties );
const userSettings = useUserSettings();
const showDemoSettings = isNotProduction() || model.consumer.canDemo;
const pairing = usePairingState();
const user = useUser();

onMounted( () =>
{
    useBackButton( hasRequiredProperties.value );
} );

/**
 * Resets the vehicle with a user prompt
 */
async function resetVehicle()
{
    const result = await useConfirm( 'Are you sure you want to reset the vehicle?', '' );
    if( result == false ) return;

    const api = useVehicleApi();
    if( await api.reset() )
    {
        location.reload();
    }
}

/**
 * Occurs when the health monitor alert gets toggle
 */
function onChangeHasHealthMonitor()
{
    if( userSettings.healthMonitorEnabledState != AutoInstalledState.Installed )
    {
        userSettings.hasHealthMonitorWarning = false;
    }
    reloadMyGarage();
}

/**
 * Occurs when the health monitor alert gets toggle
 */
function onChangeHealthMonitorAlert()
{
    model.carePlan.healthMonitor.toggleMockErrorCode( userSettings.hasHealthMonitorWarning );
    reloadMyGarage();
}

function reloadMyGarage()
{
    reloadMyGarageViewModel();
}

function onSampleVehicleSelected( vehicle: any )
{

    // Clear local storage so the user comes in fresh
    localStorage.clear();

    // Reload with the vehicle applied to the querystring
    const host = window.location.protocol + "//" + window.location.host;
    const sign = isDevelopment() ? vehicle.signForDev : isUAT() ? vehicle.signForUat : isLocal() ? vehicle.signForLocal : '';
    const query = `appConsumerId=${ vehicle.appConsumerId }&appMode=${ vehicle.appMode }&appVehicleId=${ vehicle.appVehicleId }&gpsLat=${ vehicle.gpsLat }&gpsLong=${ vehicle.gpsLong }&nonce=${ vehicle.nonce }&sign=${ sign }&timestamp=${ vehicle.timestamp }`;
    window.location.href = `${ host }/my-garage?${query}`;

}

</script>

<template>
 
    <div class="bg-gray-50/70 dark:bg-gray-900 text-gray-800 dark:text-white
                text-base lg:text-lg
                w-full h-full overflow-y-auto overflow-x-hidden flex flex-col pb-12">
                    
        <div class="bg-gradient-to-b dark:from-gray-900 dark:to-gray-850 pb-4 3xl:pb-8">

            <div class="p-6 pb-0 text-center">
                <h2 class="text-xl 3xl:text-2xl tracking-wider flex gap-2 justify-center items-center">
                    My Garage Settings
                </h2>
                <p class="text-sm lg:text-base 3xl:text-xl pt-4 opacity-60">
                    Use the settings to setup your garage
                </p>
            </div>   

        </div>

        <div class="dark:bg-gray-850">

            <VehicleHeaderRow class="px-4 lg:px-6">
                <div class="basis-full">
                    <h1 class="tracking-wider">
                        Reverse Condition Indicators
                    </h1>
                    <div class="text-sm 3xl:text-base text-gray-500 -mt-0.5 uppercase">
                        Changes the direction of the condition reminders
                    </div>
                </div>
                <Switch v-model="userSettings.reverseConditionIndicators"
                        color="secondary"
                        density="compact"
                        class="mr-3"
                        hide-details />
            </VehicleHeaderRow>

            <VehicleHeaderRow class="px-4 lg:px-6">
                <div class="basis-full">
                    <h1 class="tracking-wider">
                        Expert Mode
                    </h1>
                    <div class="text-sm 3xl:text-base text-gray-500 -mt-0.5 uppercase">
                        Show more detailed information about your car and its condition
                    </div>
                </div>
                <Switch v-model="user.isExpert"
                        color="secondary"
                        density="compact"
                        class="mr-3"
                        hide-details />
            </VehicleHeaderRow>

            <VehicleHeaderRow class="px-4 lg:px-6">
                <div class="basis-full">
                    <h1 class="tracking-wider">
                        Health Monitor
                    </h1>
                    <div class="text-sm 3xl:text-base text-gray-500 -mt-0.5 uppercase">
                        Set installation status
                    </div>
                </div>
                <SelectList v-model="userSettings.healthMonitorEnabledState"
                            required
                            density="comfortable"
                            hide-details
                            class="shrink-0"
                            :items="AutoInstalledState.toListItems()" 
                            @update:modelValue="() => onChangeHasHealthMonitor()"
                />

            </VehicleHeaderRow>

            <VehicleHeaderRow v-if="model.carePlan.carbucksRewards.isConciergeEnabled && model.carePlan.carbucksRewards.isConciergeMessagesEnabled"
                              class="px-4 lg:px-6">
                <div class="basis-full">
                    <h1 class="tracking-wider">
                        Show Concierge Messages
                    </h1>
                    <div class="text-sm 3xl:text-base text-gray-500 -mt-0.5 uppercase">
                        Display concierge messages when available
                    </div>
                </div>
                <Switch v-model="userSettings.showConciergeMessages"
                        color="secondary"
                        density="compact"
                        class="mr-3"
                        hide-details />
            </VehicleHeaderRow>
            
        </div>

        <!-- DEMO VEHICLES -->
        <div v-if="showDemoSettings">

            <div class="px-6 pt-6 3xl:pt-10 pb-2 3xl:pb-4 text-center 
                        bg-gradient-to-b dark:from-gray-850 dark:to-gray-850/60">
                <div class="text-center">
                    <h2 class="text-xl tracking-wider">Demo Settings</h2>
                </div>
            </div>
            
            <div class="dark:bg-gray-850">
            
                <SettingsRow title="Has Example CarePlan" subtitle="Enables mock CarePlan settings">
                    <Switch v-model="userSettings.enableDemoCarePlanSettings"
                            color="secondary"
                            density="compact"
                            class="mr-3"
                            hide-details />
                </SettingsRow>

                <SettingsRow title="Has Transactions" subtitle="Enables mock transaction data">
                    <Switch v-model="userSettings.enableDemoTransactions"
                            color="secondary"
                            density="compact"
                            class="mr-3"
                            hide-details />
                </SettingsRow>

                <SettingsRow title="Has Linked Cards" subtitle="Enables mock linked cards">
                    <Switch v-model="userSettings.enableDemoLinkedCards"
                            color="secondary"
                            density="compact"
                            class="mr-3"
                            hide-details />
                </SettingsRow>

                <SettingsRow title="Has Bank Account" subtitle="Enables a mock bank account">
                    <Switch v-model="userSettings.enableDemoBankAccount"
                            color="secondary"
                            density="compact"
                            class="mr-3"
                            hide-details />
                </SettingsRow>

                <SettingsRow title="First Marketplace Visit" subtitle="Set the marketplace to first visit">
                    <Switch v-model="userSettings.isFirstMarketplaceVisit"
                            color="secondary"
                            density="compact"
                            class="mr-3"
                            hide-details />
                </SettingsRow>

                <SettingsRow title="Has Health Monitor Warning" subtitle="Adds a mock health monitor warning"
                             :disabled="userSettings.healthMonitorEnabledState != AutoInstalledState.Installed"
                             :class="{ 'opacity-30': userSettings.healthMonitorEnabledState != AutoInstalledState.Installed }">
                    <Switch v-model="userSettings.hasHealthMonitorWarning"
                            @change="() => onChangeHealthMonitorAlert()"
                            color="secondary"
                            density="compact"
                            class="mr-3"
                            hide-details />
                </SettingsRow>

                <SettingsRow title="Has Theft Recovery Device" subtitle="Does the user have a Theft Recovery device?">
                    <Switch v-model="userSettings.hasTheftRecoveryDevice"
                            @change="() => reloadMyGarage()"
                            color="secondary"
                            density="compact"
                            class="mr-3"
                            hide-details />
                </SettingsRow>

                <DeveloperGpsCoordinates />

                <SettingsRow title="Reset Health Monitor Check" 
                             subtitle="Reset the Health Monitor check if it has been skipped"
                             class="transition-opacity duration-300 ease-in"
                             :disabled="userSettings.skipHealthMonitorReminderUntil == null"
                             :class="{'opacity-30': userSettings.skipHealthMonitorReminderUntil == null }">
                    <Button variant="text"
                            class="opacity-60 -mr-4"
                            @click="() => pairing.cancelSkip()">
                        Reset
                    </Button>
                </SettingsRow>

                <SettingsRow title="Reset Current Vehicle" subtitle="Restore original seed data">
                    <Button variant="text"
                            class="opacity-60 -mr-4"
                            @click="() => resetVehicle()">
                        Reset
                    </Button>
                </SettingsRow>

            </div>

            <div class="px-6 pt-6 3xl:pt-10 pb-2 3xl:pb-4 text-center 
                        bg-gradient-to-b dark:from-gray-850 dark:to-gray-850/60">
                <div v-if="hasRequiredProperties" class="text-center">
                    <h2 class="text-xl tracking-wider">Demo Vehicles</h2>
                </div>
            </div>

            <div class="dark:border-t border-gray-800
                        bg-white dark:bg-gray-850">

                <SettingsRow v-for="vehicle in SampleVehicles" :title="vehicle.name" :subtitle="vehicle.brand + ' | Rego: ' + vehicle.rego">
                    <Button variant="text"
                            class="opacity-60 -mr-4"
                            @click="() => onSampleVehicleSelected( vehicle )">
                        Switch
                    </Button>
                </SettingsRow>

            </div>
        </div>
                
    </div>

</template>
